import { renderRoot } from 'quartz-core/client';

//I18n Configuration
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import registerAllData from 'I18n/init/register/lazy/registerAllData';
import lang from 'i2l?mode=very-lazy&query=sporks!automation-ui-canvas/lang/en.lyaml';
import 'I18n/init/data/currency';
import { ApolloProvider } from '@apollo/client';
import { createApolloClient } from 'hubspot-apollo-client';
import auth from 'automation-ui-canvas/utils/Auth';
import PortalIdParser from 'PortalIdParser';
import Application from 'automation-ui-canvas/containers/Application';

// Redux
import 'redux';
import { Provider } from 'react-redux';
import configureStore from 'automation-ui-canvas/utils/configureStore';

// Configure the router
import { BrowserRouter, Route } from 'react-router-dom';
import { getRootUrl } from 'automation-ui-canvas/utils/urls';
import historyAdapter from 'react-rhumb/historyAdapterV4';
import { RhumbProvider } from 'react-rhumb';
import rhumbConfig from 'automation-ui-canvas/rhumb-config.yaml';
import { I18nProvider } from 'automation-ui-i18n/I18nContext';

// Monitoring and Performance
import Raven from 'raven-js';
import { UsageTrackerProvider } from 'automation-ui-usage-tracking/UsageTrackerContext';
import ErrorBoundary from 'automation-ui-canvas/components/errors/ErrorBoundary';
import { usageTracker, immediateUsageTracker } from 'automation-ui-canvas/utils/trackingUtils';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import { FETCH_LOG_EVENTS_FIELDNAME, FETCH_LOG_EVENTS_FIELD_POLICY } from 'automation-ui-log-events/api-client/queries/LogEventQueries';
import { OBJECT_PROPERTIES_BY_OBJECT_FIELDNAME, OBJECT_PROPERTIES_BY_LIST_FIELDNAME, GET_OBJECT_PROPERTIES_FIELD_POLICY } from 'automation-ui-enrollment/queries/ObjectPropertiesQueries';
import { FLOW_REVISIONS_FIELDNAME, GET_FLOW_REVISIONS_FIELD_POLICY } from 'automation-ui-management/queries/flowRevisionQueries';
import { AuthProvider } from 'automation-ui-core/context/AuthContext';
import { cacheMultiCurrencyRequests } from 'automation-ui-canvas/utils/multiCurrencyRequestCacher';
import { externalDataOptionsQueryFieldPolicy } from 'automation-ui-action-features/actions/extension/externalDataQueries';
import { workflowsQueryFieldPolicy } from 'automation-ui-action-features/actions/enroll-in-flow/workflowQueries';
import { actionSetsQueryFieldPolicy } from 'automation-ui-canvas/components/action-set/ActionSetQueries';
import { UnblockHistoryProvider } from 'automation-ui-canvas/containers/hocs/UnblockHistoryContext';
import { CustomActionDependencyProvider } from 'automation-ui-custom-action-sdk/context/CustomActionDependencyContext';
import { enrollmentHistoryQueryFieldPolicy } from 'automation-ui-canvas/containers/enrollment-history/panel/queries/fieldPolicy';
import { CopilotCollaborationProvider } from 'automation-ui-canvas/copilot-collaboration/provider/CopilotCollaborationProvider';
import { jsx as _jsx } from "react/jsx-runtime";
const dataFetchingClient = new DataFetchingClient({
  queryFieldPolicies: Object.assign({
    [FETCH_LOG_EVENTS_FIELDNAME]: FETCH_LOG_EVENTS_FIELD_POLICY,
    [FLOW_REVISIONS_FIELDNAME]: GET_FLOW_REVISIONS_FIELD_POLICY,
    [OBJECT_PROPERTIES_BY_OBJECT_FIELDNAME]: GET_OBJECT_PROPERTIES_FIELD_POLICY,
    [OBJECT_PROPERTIES_BY_LIST_FIELDNAME]: GET_OBJECT_PROPERTIES_FIELD_POLICY
  }, externalDataOptionsQueryFieldPolicy, actionSetsQueryFieldPolicy, workflowsQueryFieldPolicy, enrollmentHistoryQueryFieldPolicy),
  assumeImmutableResults: true
});
const StandardI18nProvider = createStandardI18nProvider();
const apolloClient = createApolloClient({
  connectToDevTools: true
});
export default class App {
  constructor() {
    this.auth = undefined;
    this.store = undefined;
  }
  // Kicks off the app. This is intentionally not in the constructor just in
  // case there is a reason you need to explictly call init (and don't want it
  // auto-called by importing this module)
  start() {
    if (window.jasmine) return;
    cacheMultiCurrencyRequests();
    // @ts-expect-error claims AppInstance doesn't exist on window
    window.AppInstance = this;
    this.init().then(() => {
      this.startRedux();
      this.startRouter();
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }

  // Performs all the initialization needed before the main app code can run/render.
  init() {
    const langAvailablePromise = Promise.all([StandardI18nProvider.register(lang), registerAllData(StandardI18nProvider)]);

    // Kick off the auth process
    const authenticate = auth.init();

    // eslint-disable-next-line promise/catch-or-return,@typescript-eslint/no-floating-promises
    authenticate.then(({
      user,
      portal,
      userObjectTimezone
    }) => {
      StandardI18nProvider.setLocale({
        locale: user.locale,
        langEnabled: user.lang_enabled,
        timezone: portal.timezone,
        userObjectTimezone
      });
      Raven.setUserContext({
        // @ts-expect-error expects `number` id, but seems to work fine as a string
        id: user.user_id,
        email: user.email,
        locale: user.locale
      });
    });

    // Sets hubspot.portal.id (unless `{ preserveGlobalId: true }` was passed)
    PortalIdParser.get();

    // Return a promise that is resolved when every promise in Auth initializers
    // and I18n has resolved. AKA, when everything is ready.
    return Promise.all([authenticate, langAvailablePromise]);
  }
  startRedux() {
    const initialState = auth.get();
    this.auth = initialState;
    this.store = configureStore({
      auth: initialState
    });
  }
  startRouter() {
    renderRoot( /*#__PURE__*/_jsx(I18nProvider, {
      value: StandardI18nProvider,
      children: /*#__PURE__*/_jsx(AuthProvider, {
        value: this.auth,
        children: /*#__PURE__*/_jsx(Provider, {
          store: this.store,
          children: /*#__PURE__*/_jsx(ApolloProvider, {
            client: apolloClient,
            children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
              client: dataFetchingClient,
              children: /*#__PURE__*/_jsx(UsageTrackerProvider, {
                value: {
                  usageTracker,
                  immediateUsageTracker
                },
                children: /*#__PURE__*/_jsx(CopilotCollaborationProvider, {
                  children: /*#__PURE__*/_jsx(CustomActionDependencyProvider, {
                    privateDependencies: {
                      i18nProvider: StandardI18nProvider
                    },
                    children: /*#__PURE__*/_jsx(UnblockHistoryProvider, {
                      children: /*#__PURE__*/_jsx(BrowserRouter, {
                        basename: getRootUrl(),
                        children: /*#__PURE__*/_jsx(Route, {
                          children: ({
                            history
                          }) => /*#__PURE__*/_jsx(RhumbProvider, {
                            config: rhumbConfig // cant access `PartialRouteSpecs`
                            ,
                            history: historyAdapter(history),
                            children: /*#__PURE__*/_jsx(ErrorBoundary, {
                              boundaryName: "Application",
                              children: /*#__PURE__*/_jsx(Application, {})
                            })
                          })
                        })
                      })
                    })
                  })
                })
              })
            })
          })
        })
      })
    }));
  }
}